import Loading from '@/common/atoms/Loading'
import TextErrors from '@/common/atoms/TextErrors'
import useCurrentMember from '@/member/hooks/useCurrentMember'
import useOrgMember from '@/member/hooks/useOrgMember'
import { Box, useColorMode } from '@chakra-ui/react'
import { useOrgWhiteboardQuery } from '@gql'
import React, { lazy, Suspense, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import useWhiteboardSave from '../hooks/useWhiteboardSave'
import type { WhiteboardElements } from './WhiteboardCanvas'
import { WhiteboardSaveState } from '../whiteboardSaver'

const WhiteboardCanvas = lazy(() => import('./WhiteboardCanvas'))

interface Props {
  orgId: string
  // The save status, shown by the org chart in its options bar
  onSaveStateChange(state: WhiteboardSaveState): void
}

// The org's whiteboard (#200), a view of the org chart. Members draw on it,
// read-only members look at it; every change is saved in the database, and
// the status always says where the saving stands.
export default function WhiteboardView({ orgId, onSaveStateChange }: Props) {
  const { i18n } = useTranslation()
  const { colorMode } = useColorMode()
  const canEdit = useOrgMember()
  const currentMember = useCurrentMember()

  // Fresh from the database each time the view opens: it is the only memory
  const { data, error } = useOrgWhiteboardQuery({
    variables: { orgId },
    fetchPolicy: 'network-only',
  })
  const { saver, state, savedId } = useWhiteboardSave<WhiteboardElements>(orgId)

  useEffect(() => onSaveStateChange(state), [state])

  // Saving starts once the saved board is on screen, never before: an empty
  // canvas must not overwrite it
  const loaded = !!data
  useEffect(() => {
    if (loaded && canEdit) saver?.markLoaded()
  }, [loaded, canEdit, saver])

  if (error) return <TextErrors errors={[error]} />
  if (!data || !saver) return <Loading active center />

  // The room of the live drawing is named after the board's random id, which
  // only members can read: the collab server checks no one (#200). A board
  // drawn for the first time gets its id from that first save.
  const board = data.org_whiteboard[0]

  return (
    <Box position="relative" h="100%">
      <Suspense fallback={<Loading active center />}>
        <WhiteboardCanvas
          initialElements={board?.elements}
          readOnly={!canEdit}
          language={i18n.language}
          colorMode={colorMode}
          roomId={board?.id ?? savedId}
          userName={currentMember?.name}
          onChange={saver.change}
        />
      </Suspense>
    </Box>
  )
}
